import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";

//-----------------------------------------------------------------
export function ResizableGrid(props) {
  //-----------------------------------------------------------------
  //#region [VALUES]

  const {
    key,
    columns,
    rows,
    resizable = "both",
    dividerSize = 8,
    dividerColor = 'black',
    dividerTrim  = 'white',
    initialColSizes,
    initialRowSizes,
    colMinPixelSize = 25,
    rowMinPixelSize = 25,
    sizes,           // controlled { cols, rows }
    onChange,
    renderCell,
    style,
  } = props;

  const container_ref = useRef(null);

  const is_resizable_cols = resizable === "columns" || resizable === "both";
  const is_resizable_rows = resizable === "rows"    || resizable === "both";

  const is_controlled = Boolean(sizes);

  const col_min_sizes = useMemo( () => createMinSizes(columns, colMinPixelSize), [colMinPixelSize, columns] );
  const row_min_sizes = useMemo( () => createMinSizes(   rows, rowMinPixelSize), [rowMinPixelSize,    rows] );

  const col_percentages_default = useMemo( () => equalPercents( columns ), [columns] );
  const row_percentages_default = useMemo( () => equalPercents( rows    ), [rows   ] );

  const [col_percentages_internal, setColPercentagesInternal] = useState(() =>
    normalizePercents(initialColSizes, columns, col_percentages_default)
  );
  const [row_percentages_internal, setRowPercentagesInternal] = useState(() =>
    normalizePercents(initialRowSizes,    rows, row_percentages_default)
  );

  const col_percentages = createPercentages( sizes?.cols, columns, col_percentages_default, col_percentages_internal );
  const row_percentages = createPercentages( sizes?.rows,    rows, row_percentages_default, row_percentages_internal );

  // === SIZE TRACKING ===
  //-----------------------------------------------------------------
  const [container_size, setContainerSize] = useState({ width: 0, height: 0 });

  const col_divider_total = (columns - 1) * dividerSize;
  const row_divider_total = (rows    - 1) * dividerSize;
  const available_width  = Math.max( 0, container_size.width  - col_divider_total );
  const available_height = Math.max( 0, container_size.height - row_divider_total );

  const col_pixels = useMemo( () => calcPixelSizes(col_percentages, available_width  ), [col_percentages, available_width ] );
  const row_pixels = useMemo( () => calcPixelSizes(row_percentages, available_height ), [row_percentages, available_height] );
  
  // === GRID TEMPLATE BUILDERS ===
  //-----------------------------------------------------------------

  // Weights from percentages; ensure sum>0 for tracks
  const row_weights = useMemo(() => calcWeights( row_percentages ), [row_percentages]);
  const col_weights = useMemo(() => calcWeights( col_percentages ), [col_percentages]);

  const row_template = useMemo(() => createTemplate(    rows, row_min_sizes, row_weights, dividerSize ), [   rows, row_min_sizes, row_weights, dividerSize]);
  const col_template = useMemo(() => createTemplate( columns, col_min_sizes, col_weights, dividerSize ), [columns, col_min_sizes, col_weights, dividerSize]);

  // === EVENT CALLBACKS ===
  //-----------------------------------------------------------------

  // --- UPDATE HELPERS
  const updatePercentagesCol_cb = useCallback( updatePercentagesCol, [is_controlled, columns, col_percentages_default, onChange, row_percentages] );
  const updatePercentagesRow_cb = useCallback( updatePercentagesRow, [is_controlled,    rows, row_percentages_default, onChange, col_percentages] );

  // --- DRAG STATE & HANDLERS
  const dragRef = useRef({
    kind           : null , /** "col" | "row" */ 
    index          :   -1 ,
    start_position :    0 ,
    start_pixels   :   [] ,
  });

  const onPointerDown_cb = useCallback( onPointerDown, [is_resizable_rows, row_pixels, is_resizable_cols, col_pixels] );
  const onPointerMove_cb = useCallback( onPointerMove, [available_width, available_height, col_min_sizes, row_min_sizes, updatePercentagesCol_cb, updatePercentagesRow_cb] );
  const onDoubleClick_cb = useCallback( onDoubleClick, [available_width, available_height, col_min_sizes, row_min_sizes, updatePercentagesCol_cb, updatePercentagesRow_cb, row_pixels, col_pixels, is_resizable_rows, is_resizable_cols] );

  // === RENDER ELEMENTS ===
  //-----------------------------------------------------------------
  const cells = useMemo( renderCells, [rows, columns, renderCell] );

  //#endregion

  //#region [EFFECT]

  // NEW: Do an immediate synchronous measurement so first paint is non-zero.
  useLayoutEffect(() => {
    const container = container_ref.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    if (rect.width > 0 || rect.height > 0) {
      setContainerSize({ width: rect.width, height: rect.height });
    }
  }, []);

  useLayoutEffect(() => {
    const container = container_ref.current;
    if (!container) return;

    // ResizeObserver keeps the layout responsive.
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0].contentRect;
      setContainerSize({ width: rect.width, height: rect.height });
    });

    observer.observe(container);

    return () => {
      observer.disconnect();
    }
  }, []);

  //#endregion

  //#region [METHOD]

  //-----------------------------------------------------------------
  function createMinSizes( count, defaults ) {
    //-----------------------------------------------------------------
    if (Array.isArray(defaults)) return padArray(defaults, count, 60);
    return new Array(count).fill(defaults);
  }

  //-----------------------------------------------------------------
  function createPercentages( source, count, defaults, internal_values ) {
    //-----------------------------------------------------------------
    if( is_controlled ) {
      return normalizePercents( source, count, defaults )
    }
    
    return internal_values;
  }

  //-----------------------------------------------------------------
  function calcPixelSizes( percentages, available_space ) {
    //-----------------------------------------------------------------
    return percentages.map( (percent) => (available_space > 0 ? (percent / 100) * available_space : 0) )
  }

  //-----------------------------------------------------------------
  function calcWeights( percentages ) {
    //-----------------------------------------------------------------
    const weights = percentages.map( (percent) => ( Math.max(0, percent) ));
    const sum     = weights.reduce( (a, b) => a + b, 0 );
    if (sum <= 0) {
      // If everything is zero, give a tiny weight to the last track
      const fallback = weights.slice();
      return fallback;
    }
    return weights.map((v) => v / sum); // normalize to 1.0
  }

  //-----------------------------------------------------------------
  function createTemplate( count, min_size, weights, divider_size ) {
    //-----------------------------------------------------------------
    if (count <= 0) return "none";

    const parts = [];
    for (let index = 0; index < count; index++) {
      const min_pixel = Array.isArray(min_size) ? min_size[index] : min_size;
      const weight    = Math.max(0.0001, weights[index]); // avoid 0fr

      parts.push(`minmax(${Math.max(0, min_pixel)}px, ${weight}fr)`);

      if (index < count - 1) parts.push(`${divider_size}px`);
    }

    return parts.join(" ");
  }

  // === UTILITIES ===

  //-----------------------------------------------------------------
  function padArray(array, n, fill) {
    //-----------------------------------------------------------------
    const result = array.slice(0, n);
    while (result.length < n) result.push(fill);
    return result;
  }

  //-----------------------------------------------------------------
  function equalPercents(n) {
    //-----------------------------------------------------------------
    if (n <= 0) return [];

    const base   = Math.floor(100 / n);
    const result = Array(n).fill(base);
    const sum    = result.reduce( (a, b) => a + b, 0 );

    // Add any left over space from rounding values to the first entry;
    //  33.33 x 3 => 34 | 33 | 33
    result[0] += 100 - sum;
    return result;
  }

  //-----------------------------------------------------------------
  function normalizePercents(values, count, fallback) {
    //-----------------------------------------------------------------
    const results = Array.isArray(values) && values.length === count ? [...values] : [...fallback];
    
    let sum = 0;
    for (let index = 0; index < count; index++) {
      const value = Number( results[index] );
      results[index] = isFinite(value) ? Math.max(0, value) : 0;
      sum += results[index];
    }

    if (sum <= 0) return equalPercents(count);

    const scaled = results.map((v) => (v / sum) * 100);
    const diff   = 100 - scaled.reduce((a, b) => a + b, 0);

    scaled[0] += diff;
    return scaled;
  }

  //-----------------------------------------------------------------
  function pixelToPercent(pixels=[], total_pixels) {
    //-----------------------------------------------------------------
    if (total_pixels <= 0) return equalPercents(pixels.length);

    const percents = pixels.map((x) => (x / total_pixels) * 100);
    const sum      = percents.reduce((a, b) => a + b, 0);

    percents[0] += 100 - sum;
    return percents;
  }

  //-----------------------------------------------------------------
  function clamp(value, min, max) {
    //-----------------------------------------------------------------
    return Math.min( Math.max( value, min ), max );
  }

  //#endregion

  //#region [EVENTS]

  //-----------------------------------------------------------------
  function updatePercentagesCol(next) {
    //-----------------------------------------------------------------
    const normalized = normalizePercents(next, columns, col_percentages_default);

    if (!is_controlled) setColPercentagesInternal(normalized);
    onChange?.({ cols: normalized, rows: row_percentages });
  }

  //-----------------------------------------------------------------
  function updatePercentagesRow(next) {
    //-----------------------------------------------------------------
    const normalized = normalizePercents(next, rows, row_percentages_default);

    if (!is_controlled) setRowPercentagesInternal(normalized);

    onChange?.({ cols: col_percentages, rows: normalized });
  }

  //-----------------------------------------------------------------
  function onPointerDown(event, index, kind='col') {
    //-----------------------------------------------------------------
    const is_row = kind === 'row';
    const is_resizable = is_row ? is_resizable_rows : is_resizable_cols;
    if (!is_resizable) return;

    const position = is_row ? event.clientY : event.clientX;
    const pixels   = is_row ?    row_pixels :    col_pixels;

    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);

    dragRef.current.kind             = kind;
    dragRef.current.index            = index;
    dragRef.current.start_position   = position;
    dragRef.current.start_pixels     = [...pixels];

    document.body.style.userSelect = "none";
  }

  //-----------------------------------------------------------------
  function onPointerMove(event) {
    //-----------------------------------------------------------------
    const { kind, index, start_position, start_pixels } = dragRef.current;
    if( dragRef.current.index < 0 ) return;

    const is_row = kind === 'row';
    const min_sizes       = is_row ?           row_min_sizes :           col_min_sizes;
    const client_position = is_row ?           event.clientY :           event.clientX;
    const available_space = is_row ?        available_height :         available_width;
    const update_cb       = is_row ? updatePercentagesRow_cb : updatePercentagesCol_cb;
    
    const     delta = client_position - start_position;
    const     start = start_pixels[index    ];
    const     end   = start_pixels[index + 1];
    const min_start = min_sizes[index    ]
    const min_end   = min_sizes[index + 1]

    const min_delta = -Math.max( 0, start - min_start );
    const max_delta =  Math.max( 0, end   - min_end   );
    const clamped   = clamp(delta, min_delta, max_delta);

    const next_pixel      = [...start_pixels];
    next_pixel[index    ] = start + clamped;
    next_pixel[index + 1] = end   - clamped;

    const percent = pixelToPercent(next_pixel, Math.max(1, available_space))
    update_cb(percent);
  }

  //-----------------------------------------------------------------
  function onDoubleClick(index, kind='col') {
    //-----------------------------------------------------------------
    const is_row = kind === 'row';
    const is_resizable = is_row ? is_resizable_rows : is_resizable_cols;
    if (!is_resizable) return;

    const pixels          = is_row ?              row_pixels :              col_pixels;
    const min_sizes       = is_row ?           row_min_sizes :           col_min_sizes;
    const available_space = is_row ?        available_height :         available_width;
    const update_cb       = is_row ? updatePercentagesRow_cb : updatePercentagesCol_cb;

    const sum        = pixels[index] + pixels[index + 1];
    const min_start  = min_sizes[index    ];
    const min_end    = min_sizes[index + 1];
    const half       = sum / 2;

    const next_pixel      = [...pixels];
    next_pixel[index    ] = clamp(half, min_start, sum - min_end);
    next_pixel[index + 1] = sum - next_pixel[index];

    const percent = pixelToPercent(next_pixel, Math.max(1, available_space))
    update_cb(percent);
  }

  //-----------------------------------------------------------------
  function dragEnd() {
    //-----------------------------------------------------------------
    dragRef.current.kind  = null;
    dragRef.current.index = -1;
    document.body.style.userSelect = "";
  }

  //#endregion

  //#region [RENDER]

  //-----------------------------------------------------------------
  function renderCells() {
    //-----------------------------------------------------------------
    const cells = []

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < columns; col++) {
        const gridRow    = row * 2 + 1;
        const gridColumn = col * 2 + 1;
        const index = rows > columns
          ? (row * columns) + col
          : (col *    rows) + row;
        
        cells.push(
          <div
            key  ={`cell-${row}-${col}`}
            style={{ gridRow, gridColumn, minWidth: 0, minHeight: 0, overflow: "auto" }}
          >
            {renderCell?.(row, col, index)}
          </div>
        );
      }
    }

    return cells;
  }

  //-----------------------------------------------------------------
  function renderDividers_Col() {
    //-----------------------------------------------------------------
    if( columns <= 1 ) return [];

    const dividers = [];
    for (let i = 0; i < columns - 1; i++) {
      const gridColumn = i * 2 + 2;
      const gridRow    = '1 / -1' ;
      const cursor     = !is_resizable_rows ? 'default' : 'ew-resize';

      dividers.push(
        <div
          key      = {`divider-col-${i}`}
          role     = 'separator'
          tabIndex = {0}

          style={{
            border        : '2px solid ' + dividerTrim,
            background    : dividerColor,
            gridColumn    : gridColumn  ,
            gridRow       : gridRow     ,
            cursor        : cursor,
            pointerEvents : 'auto',          
          }}

          onPointerDown   = {(e) => onPointerDown_cb(e, i, 'col') }
          onPointerMove   = {       onPointerMove_cb              }
          onPointerUp     = {       dragEnd                       }
          onPointerCancel = {       dragEnd                       }
          onDoubleClick   = {( ) => onDoubleClick_cb(i, 'col')    }

          aria-orientation = 'vertical'
          aria-label       = {`Resize column ${i + 1} and ${i + 2}`}
        >
          <span />
        </div>
      );
    }

    return dividers;
  }

  //-----------------------------------------------------------------
  function renderDividers_Row() {
    //-----------------------------------------------------------------
    if( rows <= 1 ) return [];

    const dividers = [];
    for (let i = 0; i < rows - 1; i++) {
      const gridRow    = i * 2 + 2;
      const gridColumn = '1 / -1';
      const cursor     = !is_resizable_rows ? 'default' : 'ns-resize';

      dividers.push(
        <div
          key      = {`divider-row-${i}`}
          role     = 'separator'
          tabIndex = {0}

          style = {{
            border        : '2px solid ' + dividerTrim,
            background    : dividerColor,
            gridColumn    : gridColumn  ,
            gridRow       : gridRow     ,
            cursor        : cursor      ,
            pointerEvents : 'auto'      ,
          }}

          onPointerDown   = {(e) => onPointerDown_cb(e, i, 'row') }
          onPointerMove   = {       onPointerMove_cb              }
          onPointerUp     = {       dragEnd                       }
          onPointerCancel = {       dragEnd                       }
          onDoubleClick   = {( ) => onDoubleClick_cb(i, 'row')    }

          aria-orientation = 'horizontal'
          aria-label       = {`Resize row ${i + 1} and ${i + 2}`}
        >
          <span />
        </div>
      );
    }

    return dividers;
  }

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    const dividers_col = renderDividers_Col();
    const dividers_row = renderDividers_Row();
    
    return (
      <div
        ref   = {container_ref}
        key   = {key}
        style = {{
          ...style,
          display             : 'grid',
          gridTemplateColumns : col_template,
          gridTemplateRows    : row_template,
        }}
      >
        {cells}
        {is_resizable_cols && dividers_col}
        {is_resizable_rows && dividers_row}
      </div>
    );
  }

  //#endregion

  return display();
}